/**
 * A photo resized in the browser before upload: at most `maxSide` px on the long side, re-encoded as
 * JPEG. A phone photo of 4-8 MB comes out at a few hundred KB, which is all a reviewer needs and
 * keeps the upload quick on a mobile connection.
 *
 * Falls back to the original file when the browser cannot decode it (the server then decides).
 */
export async function compressImage(file: File, maxSide = 1600, quality = 0.82): Promise<Blob> {
  try {
    const bitmap = await createImageBitmap(file)
    const scale = Math.min(1, maxSide / Math.max(bitmap.width, bitmap.height))
    const width = Math.round(bitmap.width * scale)
    const height = Math.round(bitmap.height * scale)
    const canvas = document.createElement('canvas')
    canvas.width = width
    canvas.height = height
    const ctx = canvas.getContext('2d')
    if (!ctx) return file
    // White under a transparent PNG, rather than JPEG's black.
    ctx.fillStyle = '#fff'
    ctx.fillRect(0, 0, width, height)
    ctx.drawImage(bitmap, 0, 0, width, height)
    bitmap.close()
    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/jpeg', quality))
    // Keep the original if re-encoding somehow made it bigger.
    return blob && blob.size < file.size ? blob : file
  } catch {
    return file
  }
}
